'use client'

import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import Image from 'next/image'

interface Logo {
  src: string
  alt: string
  className?: string
  width: number
}

interface ScrollingLogosProps {
  logos: Logo[]
  duration?: number
  gap?: number
  className?: string
}

export default function ScrollingLogos({ 
  logos, 
  duration = 20, 
  gap = 96,
  // className = "overflow-hidden w-full max-w-[1920px] mx-auto relative h-[30px] md:mt-[40px] mt-[20px] flex items-center"
  className = "overflow-hidden w-full max-w-[1920px] mx-auto relative h-[80px] flex items-center"
}: ScrollingLogosProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<gsap.core.Timeline | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    // Check for prefers-reduced-motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      return // Don't animate if user prefers reduced motion
    }

    // Wait for all images to load before initializing animation
    const waitForImages = (callback: () => void) => {
      const containers = containerRef.current?.querySelectorAll('.icons-container')
      if (!containers || containers.length < 2) {
        // Retry after a delay if containers aren't ready
        setTimeout(() => waitForImages(callback), 100)
        return
      }

      const images = containerRef.current?.querySelectorAll('img')
      if (!images || images.length === 0) {
        // No images found, proceed anyway
        callback()
        return
      }

      let loadedCount = 0
      const totalImages = images.length
      let fallbackTimeout: NodeJS.Timeout | null = null
      
      // Function to check if all images are loaded
      const checkAllLoaded = () => {
        if (loadedCount === totalImages) {
          if (fallbackTimeout) {
            clearTimeout(fallbackTimeout)
          }
          callback()
        }
      }
      
      // Check if images are already loaded
      images.forEach((img) => {
        const imageElement = img as HTMLImageElement
        if (imageElement.complete && imageElement.naturalHeight !== 0) {
          loadedCount++
        } else {
          imageElement.addEventListener('load', () => {
            loadedCount++
            checkAllLoaded()
          }, { once: true })
          imageElement.addEventListener('error', () => {
            // Count errors as loaded to prevent infinite waiting
            loadedCount++
            checkAllLoaded()
          }, { once: true })
        }
      })

      // Check if all images were already loaded
      if (loadedCount === totalImages) {
        callback()
        return
      }

      // Fallback timeout in case some images never load
      fallbackTimeout = setTimeout(() => {
        callback()
      }, 2000)
    }

    // Small delay to ensure containers are fully rendered
    const initAnimation = () => {
      const containers = containerRef.current?.querySelectorAll('.icons-container')
      if (!containers || containers.length < 2) return

      // Calculate the width of one container
      const containerWidth = containers[0].getBoundingClientRect().width + gap
      
      // Validate container width - if it's 0 or too small, retry after a delay
      if (containerWidth <= gap) {
        setTimeout(initAnimation, 100)
        return
      }
    
      // Set initial positions - first container at 0, second at containerWidth
      gsap.set(containers[0], { x: 0 })
      gsap.set(containers[1], { x: containerWidth })

      // Simple left animation
      const createAnimation = () => {
        if (animationRef.current) {
          animationRef.current.kill()
        }

        const tl = gsap.timeline({ repeat: -1 })
        
        tl.to(containers, {
          x: `-=${containerWidth}`,
          duration: duration,
          ease: "none"
        })

        animationRef.current = tl
      }

      // Function to check and reposition containers based on their position relative to viewport
      const checkAndRepositionContainers = () => {
        if (!containerRef.current) return

        const containerRect = containerRef.current.getBoundingClientRect()
        const containerLeft = containerRect.left

        containers.forEach((container, index) => {
          const containerX = gsap.getProperty(container, "x") as number
          const containerLeftEdge = containerLeft + containerX
          const containerRightEdge = containerLeftEdge + containerWidth

          // Simple left animation repositioning
          if (containerRightEdge < containerLeft) {
            // Find the rightmost container and position this one to its right
            const otherContainer = containers[index === 0 ? 1 : 0]
            const otherContainerX = gsap.getProperty(otherContainer, "x") as number
            const otherContainerRightEdge = containerLeft + otherContainerX + containerWidth
            gsap.set(container, { x: otherContainerRightEdge - containerLeft })
          }
        })
      }

      // Start animation
      createAnimation()

      // Animation update loop to check repositioning
      const updateLoop = () => {
        checkAndRepositionContainers()
        requestAnimationFrame(updateLoop)
      }
      updateLoop()

      // Cleanup function
      return () => {
        if (animationRef.current) {
          animationRef.current.kill()
        }
      }
    }

    // Wait for images to load, then initialize animation
    waitForImages(() => {
      // Small additional delay to ensure layout is stable
      setTimeout(initAnimation, 50)
    })
    
    return () => {
      if (animationRef.current) {
        animationRef.current.kill()
      }
    }
  }, [logos, duration, gap])

  return (
    <div ref={containerRef} className={className} aria-live="off" aria-label="Client and partner logos">
      {Array.from({ length: 2 }, (_, containerIndex) => (
        <div 
          key={containerIndex} 
          className="icons-container absolute flex items-center gap-24 whitespace-nowrap w-max"
          aria-hidden={containerIndex > 0}
        >
          {logos.map((logo, logoIndex) => (
            <div
              key={`container${containerIndex + 1}-${logoIndex}`}
              className="flex-shrink-0 relative"
              style={{ width: `${logo.width}px`, height: '80px' }}
            >
              <Image
                src={logo.src}
                alt={logo.alt}
                fill
                sizes={`${logo.width}px`}
                className={`object-contain ${logo.className || ''}`}
                unoptimized
              />
            </div>
          ))}
        </div>
      ))}
      
      {/* Fade overlays on left and right */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-0 md:w-20 pointer-events-none z-10"
        style={{
          background: 'linear-gradient(to right, #FDFEF6 0%, transparent 100%)'
        }}
        aria-hidden="true"
      />
      <div 
        className="absolute right-0 top-0 bottom-0 w-0 md:w-24 pointer-events-none z-10"
        style={{
          background: 'linear-gradient(to left, #FDFEF6 0%, transparent 100%)'
        }}
        aria-hidden="true"
      />
    </div>
  )
}
